/**
 * Centralized Formatters - projectL
 *
 * This file consolidates all formatting utilities that were previously
 * scattered across multiple files. Import from here to ensure consistent
 * formatting across the application.
 *
 * @module utils/formatters
 */

// ============================================================================
// DATE & TIME FORMATTERS
// ============================================================================

/**
 * Format date for display (full format)
 * @param {string|Date} dateStr - Date to format
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.includeTime=true] - Include time
 * @param {string} [options.locale='en-IN'] - Locale
 * @returns {string} Formatted date
 * @example
 * formatDate('2024-01-15T10:30:00') // "Jan 15, 2024 10:30 AM"
 */
export function formatDate(dateStr, options = {}) {
  const { includeTime = true, locale = 'en-IN' } = options;

  if (!dateStr) return 'N/A';

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return String(dateStr);

  if (includeTime) {
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  }

  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Format date for display (date only, no time)
 * @param {string|Date} dateStr - Date to format
 * @returns {string} Formatted date
 * @example
 * formatDateOnly('2024-01-15') // "Jan 15, 2024"
 */
export function formatDateOnly(dateStr) {
  return formatDate(dateStr, { includeTime: false });
}

/**
 * Format date for invoice/formal display
 * @param {string|Date} dateStr - Date to format
 * @returns {string} Formatted date
 * @example
 * formatDateLong('2024-01-15') // "January 15, 2024"
 */
export function formatDateLong(dateStr) {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return String(dateStr);

  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format time only
 * @param {string|Date} dateStr - Date to extract time from
 * @returns {string} Formatted time
 * @example
 * formatTime('2024-01-15T10:30:00') // "10:30 AM"
 */
export function formatTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  return date.toLocaleTimeString('en-IN', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Format relative time (e.g., "2 hours ago")
 * @param {string|Date} dateStr - Date to format
 * @returns {string} Relative time string
 */
export function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return formatDateOnly(dateStr);
}

// Re-export for backward compatibility
export { formatDateTime } from './formatDateTime';

// ============================================================================
// CURRENCY FORMATTERS
// ============================================================================

/**
 * Format currency with INR symbol
 * @param {number|string} amount - Amount to format
 * @param {Object} [options] - Options
 * @param {boolean} [options.includeSymbol=true] - Include ₹ symbol
 * @param {number} [options.decimals=0] - Decimal places
 * @returns {string} Formatted currency
 * @example
 * formatCurrency(1234.56) // "₹1,235"
 * formatCurrency(1234.56, { decimals: 2 }) // "₹1,234.56"
 */
export function formatCurrency(amount, options = {}) {
  const { includeSymbol = true, decimals = 0 } = options;

  if (amount == null || isNaN(Number(amount))) {
    return includeSymbol ? '₹0' : '0';
  }

  const num = Number(amount);
  const formatted = num.toLocaleString('en-IN', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return includeSymbol ? `₹${formatted}` : formatted;
}

/**
 * Format price for display (simple, no symbol)
 * @param {number} price - Price value
 * @returns {string} Formatted price
 * @deprecated Use formatCurrency with { includeSymbol: false }
 */
export function formatPrice(price) {
  if (price == null) return '0';
  return Math.round(price).toString();
}

/**
 * Format price breakdown for invoices
 * @param {Object} breakdown - Price breakdown object
 * @returns {Object} Formatted breakdown with strings
 */
export function formatPriceBreakdown(breakdown) {
  return {
    baseFare: formatCurrency(breakdown?.baseFare || breakdown?.parcelPrice),
    distanceCharge: formatCurrency(breakdown?.distanceCharge),
    weightCharge: formatCurrency(breakdown?.weightCharge),
    platformFee: formatCurrency(breakdown?.platformFee),
    total: formatCurrency(breakdown?.total || breakdown?.totalAmount),
  };
}

// ============================================================================
// ADDRESS FORMATTERS
// ============================================================================

/**
 * Format address object to readable string
 * @param {Object|string} location - Location object or string
 * @param {Object} [options] - Options
 * @param {number} [options.maxLength] - Max string length (truncate)
 * @returns {string} Formatted address
 */
export function formatAddress(location, options = {}) {
  const { maxLength } = options;

  if (!location) return 'Not specified';
  if (typeof location === 'string') {
    return maxLength && location.length > maxLength
      ? location.substring(0, maxLength) + '...'
      : location;
  }

  // Build address from parts
  const parts = [
    location.door,
    location.street,
    location.area,
    location.city,
    location.state,
    location.pincode,
  ].filter(Boolean);

  const full = location.label || location.address || parts.join(', ') || 'Location';

  if (maxLength && full.length > maxLength) {
    return full.substring(0, maxLength) + '...';
  }

  return full;
}

/**
 * Truncate address to specified length
 * @param {Object|string} address - Address to truncate
 * @param {number} [maxLen=60] - Maximum length
 * @returns {string} Truncated address
 */
export function truncateAddress(address, maxLen = 60) {
  return formatAddress(address, { maxLength: maxLen });
}

/**
 * Format coordinates for display
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} [precision=6] - Decimal precision
 * @returns {string} Formatted coordinates
 */
export function formatCoordinates(lat, lng, precision = 6) {
  if (lat == null || lng == null) return '';
  return `${Number(lat).toFixed(precision)}, ${Number(lng).toFixed(precision)}`;
}

// ============================================================================
// DISTANCE & WEIGHT FORMATTERS
// ============================================================================

/**
 * Format distance for display
 * @param {number} km - Distance in kilometers
 * @returns {string} Formatted distance
 * @example
 * formatDistance(1.5) // "1.5 km"
 * formatDistance(0.5) // "500 m"
 */
export function formatDistance(km) {
  if (km == null || isNaN(Number(km))) return '';

  const distance = Number(km);
  if (distance < 1) {
    return `${Math.round(distance * 1000)} m`;
  }
  return `${distance.toFixed(1)} km`;
}

/**
 * Format weight for display
 * @param {number} kg - Weight in kilograms
 * @returns {string} Formatted weight
 * @example
 * formatWeight(1.5) // "1.5 kg"
 * formatWeight(0.5) // "500 g"
 */
export function formatWeight(kg) {
  if (kg == null || isNaN(Number(kg))) return '';

  const weight = Number(kg);
  if (weight < 1) {
    return `${Math.round(weight * 1000)} g`;
  }
  return `${weight.toFixed(1)} kg`;
}

/**
 * Format duration for display
 * @param {number} minutes - Duration in minutes
 * @returns {string} Formatted duration
 * @example
 * formatDuration(90) // "1h 30m"
 * formatDuration(30) // "30 min"
 */
export function formatDuration(minutes) {
  if (minutes == null || isNaN(Number(minutes))) return '';

  const mins = Math.round(Number(minutes));
  if (mins < 60) return `${mins} min`;

  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;

  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}m`;
}

/**
 * Format ETA for display
 * @param {number} minutes - ETA in minutes
 * @returns {string} Formatted ETA
 */
export function formatETA(minutes) {
  if (minutes == null || isNaN(Number(minutes))) return 'Calculating...';

  const mins = Math.round(Number(minutes));
  if (mins <= 0) return 'Arriving';
  if (mins < 60) return `${mins} min`;

  const hours = Math.floor(mins / 60);
  const remainingMins = mins % 60;

  if (remainingMins === 0) return `${hours} hr`;
  return `${hours} hr ${remainingMins} min`;
}

// ============================================================================
// PHONE & CONTACT FORMATTERS
// ============================================================================

/**
 * Format phone number for display
 * @param {string} phone - Phone number
 * @param {boolean} [includeCountryCode=false] - Include +91
 * @returns {string} Formatted phone
 * @example
 * formatPhone('9876543210') // "98765 43210"
 * formatPhone('9876543210', true) // "+91 98765 43210"
 */
export function formatPhone(phone, includeCountryCode = false) {
  if (!phone) return '';

  // Remove all non-digits
  const digits = phone.replace(/\D/g, '');

  // Remove country code if present
  const cleanPhone = digits.replace(/^91/, '');

  // Format as: XXXXX XXXXX
  if (cleanPhone.length === 10) {
    const formatted = `${cleanPhone.slice(0, 5)} ${cleanPhone.slice(5)}`;
    return includeCountryCode ? `+91 ${formatted}` : formatted;
  }

  return phone; // Return original if not 10 digits
}

/**
 * Mask phone number for privacy
 * @param {string} phone - Phone number
 * @returns {string} Masked phone
 * @example
 * maskPhone('9876543210') // "98765 ***10"
 */
export function maskPhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '').replace(/^91/, '');
  if (digits.length !== 10) return phone;
  return `${digits.slice(0, 5)} ***${digits.slice(-2)}`;
}

// ============================================================================
// STATUS FORMATTERS
// ============================================================================

/**
 * Format status for display (convert SNAKE_CASE to Title Case)
 * @param {string} status - Status string
 * @returns {string} Formatted status
 * @example
 * formatStatus('OUT_FOR_DELIVERY') // "Out for Delivery"
 */
export function formatStatus(status) {
  if (!status) return '';
  return status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Get status display color
 * @param {string} status - Status string
 * @returns {string} Color code
 */
export function getStatusColor(status) {
  const statusColors = {
    PENDING: '#ffc107',
    ASSIGNED: '#2196f3',
    PICKED_UP: '#9c27b0',
    IN_TRANSIT: '#ff9800',
    OUT_FOR_DELIVERY: '#03a9f4',
    DELIVERED: '#4caf50',
    CANCELLED: '#f44336',
    FAILED: '#f44336',
    RETURNED: '#795548',
  };
  return statusColors[status?.toUpperCase()] || '#9e9e9e';
}

// ============================================================================
// EXPORTS SUMMARY
// ============================================================================

const formatters = {
  // Date/Time
  formatDate,
  formatDateOnly,
  formatDateLong,
  formatTime,
  formatRelativeTime,

  // Currency
  formatCurrency,
  formatPrice,
  formatPriceBreakdown,

  // Address
  formatAddress,
  truncateAddress,
  formatCoordinates,

  // Distance/Weight/Duration
  formatDistance,
  formatWeight,
  formatDuration,
  formatETA,

  // Phone
  formatPhone,
  maskPhone,

  // Status
  formatStatus,
  getStatusColor,
};

export default formatters;
